import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const MAX_STEP = 32;

type UseTypewriterStreamOptions = {
  onReveal?: () => void;
};

function computeStep(backlog: number): number {
  if (backlog <= 0) return 0;
  return Math.max(1, Math.min(MAX_STEP, Math.ceil(backlog / 8)));
}

export function useTypewriterStream(
  target: string,
  active: boolean,
  options?: UseTypewriterStreamOptions,
): { displayText: string; isRevealing: boolean } {
  const [displayText, setDisplayText] = useState(target);
  const displayLenRef = useRef(target.length);
  const targetRef = useRef(target);
  const activeRef = useRef(active);
  const rafRef = useRef<number | null>(null);
  const onRevealRef = useRef(options?.onReveal);
  const cancelledRef = useRef(false);

  targetRef.current = target;
  activeRef.current = active;
  onRevealRef.current = options?.onReveal;

  useEffect(() => {
    if (active) {
      cancelledRef.current = false;
      return undefined;
    }

    cancelledRef.current = true;
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    displayLenRef.current = target.length;
    setDisplayText(target);
    return undefined;
  }, [active, target]);

  useLayoutEffect(() => {
    if (!active) {
      return undefined;
    }

    const loop = () => {
      rafRef.current = null;
      if (cancelledRef.current || !activeRef.current) {
        return;
      }

      const latestTarget = targetRef.current;
      let len = displayLenRef.current;

      if (len > latestTarget.length) {
        len = latestTarget.length;
        displayLenRef.current = len;
        setDisplayText(latestTarget);
        onRevealRef.current?.();
      } else if (len < latestTarget.length) {
        const step = computeStep(latestTarget.length - len);
        len = Math.min(latestTarget.length, len + step);
        displayLenRef.current = len;
        setDisplayText(latestTarget.slice(0, len));
        onRevealRef.current?.();
      }

      if (
        !cancelledRef.current &&
        activeRef.current &&
        displayLenRef.current < targetRef.current.length
      ) {
        rafRef.current = requestAnimationFrame(loop);
      }
    };

    if (displayLenRef.current < target.length && rafRef.current == null) {
      rafRef.current = requestAnimationFrame(loop);
    }

    return undefined;
  }, [target, active]);

  const isRevealing = active && displayText.length < target.length;

  return {
    displayText: active ? displayText : target,
    isRevealing,
  };
}
